import type { Assignment } from '@/types/domain';
import { dispatchLabel } from '@/lib/grouping';

interface BillDetailTileProps {
  assignment: Assignment;
  kind: 'pickup' | 'delivery';
  defaultOpen?: boolean;
}

const join = (parts: Array<string | null | undefined>) =>
  parts.map((p) => p?.trim()).filter((p): p is string => !!p && p.length > 0).join(', ');

/**
 * Expandable per-bill tile for the pickup/delivery detail (selection) views.
 * Mirrors the native BillDetailTile: tag + dispatch badge header, a from/to +
 * pcs/wgt subtitle, and an expandable body with full shipper/consignee context,
 * references and notes. Auto-expands when it is the only bill at the stop.
 */
export function BillDetailTile({ assignment: a, kind, defaultOpen = false }: BillDetailTileProps) {
  const isDelivery = kind === 'delivery';
  const accent = isDelivery ? 'text-brand-delivery' : 'text-brand-pickup';
  const accentBg = isDelivery ? 'bg-brand-delivery/20' : 'bg-brand-pickup/20';

  const shipperAddr = join([
    [a.remShipperAddress, a.remShipperSuite].filter(Boolean).join(' '),
    a.remShipperCity,
  ]);
  const consigneeAddr = join([
    [a.remConsigneeAddress, a.remConsigneeSuite].filter(Boolean).join(' '),
    a.remConsigneeCity,
  ]);
  const otherParty = isDelivery ? a.remShipperName : a.remConsigneeName;
  const otherLabel = isDelivery ? 'From' : 'To';

  return (
    <details
      open={defaultOpen}
      className="overflow-hidden rounded-lg border border-brand-border bg-brand-surface"
    >
      <summary className="flex cursor-pointer items-center justify-between px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base font-bold">{a.remF02Tag ?? a.remF04Id}</span>
            <span className={`rounded px-1.5 py-0.5 text-xs font-bold ${accent} ${accentBg}`}>
              {dispatchLabel(a)}
            </span>
          </div>
          <div className="mt-1 text-xs text-brand-muted">
            {otherParty ? `${otherLabel}: ${otherParty}` : ''}
          </div>
          <div className="text-xs text-brand-muted">
            {a.remPiecesSummary ?? '0'} pcs &middot; {a.remWeightSummary ?? '0'} lbs
          </div>
        </div>
      </summary>
      <div className="space-y-3 border-t border-brand-border px-4 py-3 text-sm">
        <Section label="FROM (Shipper)" accent={accent}>
          <Kv label="Company" value={a.remShipperName} />
          <Kv label="Address" value={shipperAddr} />
          {!isDelivery && <Kv label="Details" value={a.remShipperDetails} />}
          {!isDelivery && <Kv label="Phone" value={a.remShipperPhone} />}
          {isDelivery && <Kv label="City" value={a.remShipperCity} />}
        </Section>
        <Section label="TO (Consignee)" accent={accent}>
          <Kv label="Company" value={a.remConsigneeName} />
          <Kv label="Address" value={consigneeAddr} />
          <Kv label="Details" value={a.remConsigneeDetails} />
          {!isDelivery && <Kv label="Phone" value={a.remConsigneePhone} />}
        </Section>
        {!isDelivery && (
          <Section label="References" accent={accent}>
            <Kv label="Ref 1" value={a.remRef1} />
            <Kv label="Ref 2" value={a.remRef2} />
            <Kv label="Ref 3" value={a.remRef3} />
            <Kv label="Ref 4" value={a.remRef4} />
          </Section>
        )}
        {!isDelivery && <NoteBlock label="Shipper Notes" value={a.remShipperNote} accent={accent} />}
        <NoteBlock label="Consignee Notes" value={a.remConsigneeNote} accent={accent} />
      </div>
    </details>
  );
}

function Section({
  label,
  accent,
  children,
}: {
  label: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className={`text-xs font-bold uppercase tracking-wide ${accent}`}>{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Kv({ label, value }: { label: string; value?: string | null }) {
  if (!value || !value.trim()) return null;
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-20 shrink-0 text-xs text-brand-muted">{label}</span>
      <span className="text-sm">{value.trim()}</span>
    </div>
  );
}

function NoteBlock({ label, value, accent }: { label: string; value?: string | null; accent: string }) {
  if (!value || !value.trim()) return null;
  return (
    <div>
      <div className={`text-xs font-bold uppercase tracking-wide ${accent}`}>{label}</div>
      <div className="mt-1 rounded-md border border-brand-border bg-black/20 p-2 text-sm">
        {value.trim()}
      </div>
    </div>
  );
}
